import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Field, Input, Notice, Panel } from '../design/index.js'
import { formatQuantity } from '../framework/format.js'

/**
 * What a supplier sees when they follow the link in a request for quote.
 *
 * Outside the application entirely: no session, no menu, no company switcher,
 * nothing but the request they were asked to price. The token in the address
 * bar is the whole authority, so this page asks for no identity and offers no
 * way to reach anything else.
 */

interface PortalLine {
  readonly lineId: string
  readonly seq: number
  readonly item: string
  readonly description: string | null
  readonly unit: string | null
  readonly quantity: string
}

interface PortalView {
  readonly companyName: string
  readonly documentNo: string | null
  readonly documentDate: string
  readonly supplierName: string
  readonly closesOn: string
  readonly alreadySubmitted: boolean
  readonly remarks: string | null
  readonly lines: readonly PortalLine[]
}

const readError = async (response: Response): Promise<string> => {
  try {
    const body = (await response.json()) as { message?: string }
    return body.message ?? 'That did not work. Please try again.'
  } catch {
    return 'That did not work. Please try again.'
  }
}

export const QuotePortal = ({ token }: { token: string }) => {
  const [rates, setRates] = useState<Record<string, string>>({})
  const [brands, setBrands] = useState<Record<string, string>>({})
  const [validity, setValidity] = useState('30')
  const [remarks, setRemarks] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const request = useQuery({
    queryKey: ['quote', token],
    queryFn: async (): Promise<PortalView> => {
      const response = await fetch(`/api/public/rfq/${encodeURIComponent(token)}`, {
        headers: { accept: 'application/json' },
      })
      if (!response.ok) throw new Error(await readError(response))
      return (await response.json()) as PortalView
    },
    retry: false,
  })

  const submit = useMutation({
    mutationFn: async () => {
      const lines = Object.entries(rates)
        .filter(([, value]) => value.trim() !== '')
        .map(([lineId, value]) => ({
          lineId,
          rate: value.trim(),
          ...(brands[lineId]?.trim() ? { brand: brands[lineId]?.trim() } : {}),
        }))

      const response = await fetch(`/api/public/rfq/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({
          lines,
          ...(validity.trim() ? { validityDays: Number(validity) } : {}),
          ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
        }),
      })
      if (!response.ok) throw new Error(await readError(response))
      return (await response.json()) as { documentNo: string | null }
    },
    onSuccess: () => {
      setError(null)
      setDone('Thank you. Your quotation has been received.')
    },
    onError: (err: Error) => setError(err.message),
  })

  if (request.isLoading) {
    return <div className="portal"><p>Loading…</p></div>
  }

  if (request.error) {
    return (
      <div className="portal">
        <h1 className="portal__title">This link is not valid</h1>
        <p>
          It may have expired, or a newer one may have been sent to you. Please ask for a fresh link from
          whoever invited you to quote.
        </p>
      </div>
    )
  }

  const view = request.data as PortalView
  const priced = Object.values(rates).filter((r) => r.trim() !== '').length

  if (done || view.alreadySubmitted) {
    return (
      <div className="portal">
        <h1 className="portal__title">{view.companyName}</h1>
        <p className="portal__sub">Request for quote {view.documentNo}</p>
        <div style={{ marginTop: 24 }}>
          <Notice kind="ok">
            {done ?? 'A quotation has already been submitted against this link.'}
          </Notice>
        </div>
        <p style={{ marginTop: 20 }}>
          Nothing further is needed from you. {view.companyName} will be in touch about the outcome.
        </p>
      </div>
    )
  }

  return (
    <div className="portal">
      <h1 className="portal__title">{view.companyName}</h1>
      <p className="portal__sub">
        Request for quote {view.documentNo} · {view.documentDate} · for {view.supplierName}
      </p>

      <Panel title="What we need priced" aside={`Please reply by ${view.closesOn}`}>
        {view.remarks ? <p style={{ marginBottom: 16 }}>{view.remarks}</p> : null}

        <table className="portal__table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Material</th>
              <th scope="col">Quantity</th>
              <th scope="col">Your rate</th>
              <th scope="col">Brand or make</th>
            </tr>
          </thead>
          <tbody>
            {view.lines.map((line) => (
              <tr key={line.lineId}>
                <td className="num">{line.seq}</td>
                <td>
                  <div>{line.item}</div>
                  {line.description ? <div className="cell-sub">{line.description}</div> : null}
                </td>
                <td className="num">{formatQuantity(line.quantity)} {line.unit ?? ''}</td>
                <td>
                  <Input
                    value={rates[line.lineId] ?? ''}
                    onChange={(value) => setRates({ ...rates, [line.lineId]: value })}
                    align="right"
                    inputMode="decimal"
                    aria-label={`Your rate for ${line.item}`}
                  />
                </td>
                <td>
                  <Input
                    value={brands[line.lineId] ?? ''}
                    onChange={(value) => setBrands({ ...brands, [line.lineId]: value })}
                    aria-label={`Brand for ${line.item}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="portal__hint">
          Leave a rate blank for anything you are not quoting for. Rates are per unit, exclusive of tax.
        </p>
      </Panel>

      <Panel title="Your terms">
        <Field label="How long is this quotation valid, in days?">
          <Input value={validity} onChange={setValidity} inputMode="numeric" />
        </Field>
        <Field label="Anything else we should know?" hint="Freight, packing, lead time, part shipments.">
          <textarea
            className="control"
            style={{ minHeight: 96, resize: 'vertical' }}
            value={remarks}
            onChange={(e) => setRemarks(e.currentTarget.value)}
          />
        </Field>
      </Panel>

      {error ? <div style={{ margin: '18px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <div className="portal__actions">
        <Button
          kind="primary"
          disabled={submit.isPending || priced === 0}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Sending…' : `Send our quotation for ${priced} line${priced === 1 ? '' : 's'}`}
        </Button>
        <span className="portal__hint">You can send this once. Check your rates before you do.</span>
      </div>
    </div>
  )
}
